import React, { useId, useState } from "react";
import { LuChevronDown } from "react-icons/lu";

type Variant = "inline" | "panel";

type Props = {
  label: string;
  variant?: Variant;
  children: React.ReactNode;
  className?: string;
};

// A labelled show/hide with the state on the trigger, so the evidence and the working can be
// opened by keyboard and announced correctly rather than being decorative chevrons.
export default function Disclosure({ label, variant = "inline", children, className = "" }: Props) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const toggle = () => setOpen((current) => !current);

  // `panel` keeps the label on the surface when the body is closed, so evidence a visitor has not
  // opened still reads as available rather than as a link they have to notice.
  if (variant === "panel") {
    return (
      <div
        className={`tw-rounded-2xl tw-border tw-border-[#c9deed] tw-bg-white tw-p-5 ${className}`}
      >
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-4 tw-gap-y-3">
          <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-[#0068b8]">{label}</p>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={toggle}
            className="tw-shrink-0 tw-rounded-full tw-border tw-border-[#b8cee2] tw-bg-white tw-px-4 tw-py-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[.1em] tw-text-[#0068b8] tw-transition-colors hover:tw-bg-[#eaf4fc] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[#087bd3]"
          >
            {open ? "Hide research" : "View research"}
          </button>
        </div>

        <div id={panelId} hidden={!open} className="tw-mt-5 tw-border-t tw-border-[#dce8f2] tw-pt-5">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
        className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold tw-text-[#087bd3] tw-transition-colors hover:tw-text-[#0068b8] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[#087bd3]"
      >
        {label}
        <LuChevronDown
          aria-hidden
          className={`tw-h-4 tw-w-4 tw-shrink-0 tw-transition-transform tw-duration-200 ${open ? "tw-rotate-180" : ""}`}
        />
      </button>

      <div id={panelId} hidden={!open} className="tw-mt-4">
        {children}
      </div>
    </div>
  );
}
